if (!customElements.get('free-gift-picker')) {
  customElements.define(
    'free-gift-picker',
    class FreeGiftPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.buttons = Array.from(this.querySelectorAll('[data-free-gift-button]'));
        this.cards = Array.from(this.querySelectorAll('[data-free-gift-card]'));
        this.status = this.querySelector('[data-free-gift-status]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.giftVariantIds = this.parseIds(this.dataset.giftVariantIds);
        this.currentProductId = String(this.dataset.currentProductId || '');
        this.qualifyingProductIds = this.parseIds(this.dataset.qualifyingProductIds);
        this.requireCurrentProduct = this.dataset.requireCurrentProduct === 'true';
        this.blockIfGiftInCart = this.dataset.blockIfGiftInCart !== 'false';
        this.allowGiftChange = this.dataset.allowGiftChange === 'true';
        this.promoRedeemedByTheme = this.dataset.promoRedeemed === 'true';
        this.redemptionKey = this.dataset.redemptionKey || this.dataset.promoName || this.dataset.sectionId || 'free-gift-picker';
        this.autoOpenOnProductAdd = this.dataset.autoOpenOnProductAdd === 'true';
        this.displayMode = this.dataset.displayMode || 'inline';
        this.disableWhenExpired = this.dataset.disableWhenExpired === 'true';
        this.latestCart = null;
        this.loadingButton = null;
        this.lastAutoOpenItemKey = null;
        this.expired = false;

        this.buttons.forEach((button) => {
          button.addEventListener('click', this.onGiftButtonClick.bind(this));
        });

        this.setupCountdown();
        this.setupCartSubscription();
        this.syncFromCart();
      }

      disconnectedCallback() {
        if (this.cartUnsubscriber) this.cartUnsubscriber();
        if (this.countdownTimer) clearInterval(this.countdownTimer);
      }

      parseIds(rawIds) {
        return (rawIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }

      setupCartSubscription() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      async onCartUpdate(event = {}) {
        await this.syncFromCart();

        if (event.source !== 'product-form') return;
        this.openPopupAfterProductAdd(event);
      }

      setupCountdown() {
        this.countdown = this.querySelector('[data-free-gift-countdown]');
        if (!this.countdown) return;

        this.countdownEnd = Date.parse(this.countdown.dataset.endAt);
        if (Number.isNaN(this.countdownEnd)) {
          this.countdown.hidden = true;
          return;
        }

        this.countdownParts = {
          days: this.countdown.querySelector('[data-countdown-days]'),
          hours: this.countdown.querySelector('[data-countdown-hours]'),
          minutes: this.countdown.querySelector('[data-countdown-minutes]'),
          seconds: this.countdown.querySelector('[data-countdown-seconds]'),
        };

        this.updateCountdown();
        this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
      }

      updateCountdown() {
        if (!this.countdownEnd) return;

        const remaining = this.countdownEnd - Date.now();
        const nextExpired = remaining <= 0;
        const safeRemaining = Math.max(remaining, 0);
        const days = Math.floor(safeRemaining / 86400000);
        const hours = Math.floor((safeRemaining % 86400000) / 3600000);
        const minutes = Math.floor((safeRemaining % 3600000) / 60000);
        const seconds = Math.floor((safeRemaining % 60000) / 1000);

        this.setCountdownPart('days', days);
        this.setCountdownPart('hours', hours);
        this.setCountdownPart('minutes', minutes);
        this.setCountdownPart('seconds', seconds);

        if (this.expired !== nextExpired) {
          this.expired = nextExpired;
          this.applyCartState(this.latestCart);
        }
      }

      setCountdownPart(part, value) {
        if (!this.countdownParts || !this.countdownParts[part]) return;
        this.countdownParts[part].textContent = String(value).padStart(2, '0');
      }

      async syncFromCart() {
        this.setStatus(this.dataset.loadingMessage, 'loading');

        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) throw new Error('Unable to fetch cart');

          const cart = await response.json();
          this.latestCart = cart;
          this.applyCartState(cart);
        } catch (error) {
          console.error(error);
          this.setStatus(this.dataset.errorMessage, 'error');
          this.setButtonsDisabled(true);
        }
      }

      applyCartState(cart) {
        if (!cart) return;

        this.currentState = this.getCartOfferState(cart);
        this.classList.toggle('is-qualified', this.currentState.hasQualifyingItem);

        this.cards.forEach((card) => {
          const isSelected = this.currentState.giftLine
            && String(this.currentState.giftLine.variant_id || this.currentState.giftLine.id) === card.dataset.variantId;
          card.classList.toggle('is-selected', Boolean(isSelected));
        });

        if (this.currentState.promoRedeemed) {
          this.setStatus(this.dataset.redeemedMessage || this.dataset.selectedMessage, 'redeemed');
          this.updateButtons({
            disabled: true,
            selectedVariantId: this.currentState.giftLine
              ? String(this.currentState.giftLine.variant_id || this.currentState.giftLine.id)
              : null,
          });
          return;
        }

        if (this.currentState.isExpiredAndDisabled) {
          this.setStatus(this.dataset.expiredMessage, 'expired');
          this.updateButtons({ disabled: true });
          return;
        }

        if (!this.currentState.hasCurrentProduct) {
          this.setStatus(this.dataset.ineligibleMessage, 'blocked');
          this.updateButtons({ disabled: true });
          return;
        }

        if (this.blockIfGiftInCart && this.currentState.giftLine) {
          this.setStatus(this.dataset.selectedMessage, 'selected');
          this.updateButtons({
            allowChange: this.allowGiftChange,
            disabled: !this.allowGiftChange,
            selectedVariantId: String(this.currentState.giftLine.variant_id || this.currentState.giftLine.id),
          });
          return;
        }

        this.setStatus(this.dataset.readyMessage, 'ready');
        this.updateButtons({ disabled: false });
      }

      getCartOfferState(cart) {
        const items = Array.isArray(cart.items) ? cart.items : [];
        const giftLine = items.find((item) => this.giftVariantIds.includes(String(item.variant_id || item.id)));
        const promoRedeemed = this.isPromoRedeemed();
        const hasCurrentProduct = !this.requireCurrentProduct || this.hasQualifyingCartItem(items);
        const hasQualifyingItem = this.hasQualifyingCartItem(items);
        const isExpiredAndDisabled = this.expired && this.disableWhenExpired;

        return {
          giftLine,
          hasCurrentProduct,
          hasQualifyingItem,
          isExpiredAndDisabled,
          promoRedeemed,
          canClaim: !promoRedeemed && (!this.blockIfGiftInCart || !giftLine) && !isExpiredAndDisabled && hasCurrentProduct,
        };
      }

      hasQualifyingCartItem(items) {
        if (!Array.isArray(items) || !items.length) return false;

        return items.some((item) => {
          const productId = String(item.product_id || '');
          const variantId = String(item.variant_id || item.id || '');

          if (this.giftVariantIds.includes(variantId)) return false;
          if (this.qualifyingProductIds.length) return this.qualifyingProductIds.includes(productId);
          if (this.currentProductId) return productId === this.currentProductId;

          return true;
        });
      }

      openPopupAfterProductAdd(event = {}) {
        if (!this.autoOpenOnProductAdd || this.displayMode !== 'popup') return;
        if (!this.latestCart || !this.currentState || !this.currentState.canClaim) return;

        const addedItem = event.cartData || {};
        const addedProductId = addedItem.product_id ? String(addedItem.product_id) : '';

        if (addedProductId && !this.isAddedProductQualifying(addedProductId)) return;

        const autoOpenKey = addedItem.key || `${addedItem.id || event.productVariantId || 'product'}:${this.latestCart.item_count}`;
        if (this.lastAutoOpenItemKey === autoOpenKey) return;
        this.lastAutoOpenItemKey = autoOpenKey;

        window.setTimeout(() => this.showPopup(), 350);
      }

      isAddedProductQualifying(productId) {
        const productIdString = String(productId || '');

        if (!productIdString) return true;
        if (this.qualifyingProductIds.length) return this.qualifyingProductIds.includes(productIdString);
        if (this.currentProductId) return productIdString === this.currentProductId;

        return true;
      }

      showPopup() {
        const modal = this.closest('modal-dialog');
        if (!modal || modal.hasAttribute('open') || typeof modal.show !== 'function') return;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.classList.contains('active') && typeof cartDrawer.close === 'function') {
          cartDrawer.close();
        }

        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.close === 'function') {
          cartNotification.close();
        }

        const trigger = document.querySelector(`[data-free-gift-popup-trigger="${this.id}"]`) || this;
        modal.show(trigger);
      }

      updateButtons({ disabled, selectedVariantId = null, allowChange = false }) {
        this.buttons.forEach((button) => {
          const card = button.closest('[data-free-gift-card]');
          const variantId = card ? card.dataset.variantId : '';
          const available = card ? card.dataset.available === 'true' : false;
          const isSelected = selectedVariantId && selectedVariantId === variantId;
          const shouldDisable = (disabled && !(allowChange && !isSelected)) || isSelected || !available || !variantId || button === this.loadingButton;
          const label = button.querySelector('span');

          button.disabled = shouldDisable;
          button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

          if (label) {
            if (!available) {
              label.textContent = button.dataset.soldOutText || 'Sold out';
            } else if (isSelected) {
              label.textContent = button.dataset.selectedText || 'Selected';
            } else if (allowChange && selectedVariantId) {
              label.textContent = button.dataset.changeText || button.dataset.defaultText || 'Claim free product';
            } else {
              label.textContent = button.dataset.defaultText || 'Claim free product';
            }
          }
        });
      }

      setButtonsDisabled(disabled) {
        this.updateButtons({ disabled });
      }

      setStatus(message, state) {
        this.dataset.state = state;
        if (this.status && message) this.status.textContent = message;
      }

      isPromoRedeemed() {
        return this.promoRedeemedByTheme;
      }

      markPromoRedeemed() {
        try {
          window.localStorage.removeItem(this.getRedemptionStorageKey());
        } catch (error) {
          // Storage can be unavailable in private browsing. Cart state still updates normally.
        }
      }

      getRedemptionStorageKey() {
        return `freeGiftPickerRedeemed:${this.redemptionKey}`;
      }

      getVariantUrl(variantId) {
        const root = window.Shopify?.routes?.root || '/';
        return `${root.replace(/\/?$/, '/')}variants/${variantId}.js`;
      }

      formatMessage(message, variantId) {
        return (message || '').replace('[id]', variantId);
      }

      async validateVariantId(variantId) {
        const response = await fetch(this.getVariantUrl(variantId), {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error(
            this.formatMessage(
              this.dataset.invalidVariantMessage
                || 'Gift option [id] is not a valid Shopify variant ID.',
              variantId
            )
          );
        }

        const variant = await response.json();
        if (!variant || String(variant.id) !== String(variantId)) {
          throw new Error(this.formatMessage(this.dataset.invalidVariantMessage, variantId));
        }

        return variant;
      }

      getGiftLineVariantId(line) {
        return String(line?.variant_id || line?.id || '');
      }

      appendCartSections(formData) {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return;

        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      getCartSectionsBody() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return {};

        return {
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      renderCartContents(parsedState) {
        if (this.cart && parsedState.sections && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(parsedState);
        }
      }

      async removeGiftLine(giftLine) {
        if (!giftLine?.key) return;

        const config = typeof fetchConfig === 'function'
          ? fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

        config.body = JSON.stringify({
          id: giftLine.key,
          quantity: 0,
          ...this.getCartSectionsBody(),
        });

        const response = await fetch(routes.cart_change_url, config);
        const parsedState = await response.json();

        if (!response.ok || parsedState.errors) {
          throw new Error(parsedState.errors || this.dataset.errorMessage);
        }

        this.latestCart = parsedState;
        return parsedState;
      }

      async onGiftButtonClick(event) {
        const button = event.currentTarget;
        const card = button.closest('[data-free-gift-card]');
        const variantId = card ? card.dataset.variantId : '';
        const currentGiftLine = this.currentState?.giftLine;
        const currentGiftVariantId = this.getGiftLineVariantId(currentGiftLine);
        let addFailed = false;

        if (!variantId || button.disabled) return;
        if (currentGiftVariantId === variantId) return;
        if (currentGiftLine && !this.allowGiftChange) return;

        this.startButtonLoading(button);
        this.setButtonsDisabled(true);

        try {
          await this.validateVariantId(variantId);

          if (currentGiftLine) {
            await this.removeGiftLine(currentGiftLine);
          }

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          formData.append('properties[_Free gift]', 'true');

          if (this.dataset.promoName) {
            formData.append('properties[_Promotion]', this.dataset.promoName);
          }

          this.appendCartSections(formData);

          if (this.cart && typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(button);
          }

          const config = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/json' } };

          config.headers = config.headers || {};
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          const response = await fetch(routes.cart_add_url, config);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || parsedState.message || this.dataset.errorMessage);
          }

          this.renderCartContents(parsedState);

          this.markPromoRedeemed();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'free-gift-picker',
              productVariantId: variantId,
              cartData: parsedState,
            });
          }

          await this.syncFromCart();
        } catch (error) {
          addFailed = true;
          console.error(error);
          this.setStatus(error.message || this.dataset.errorMessage, 'error');
        } finally {
          this.stopButtonLoading(button, !addFailed);
        }
      }

      startButtonLoading(button) {
        this.loadingButton = button;
        button.classList.add('loading');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
      }

      stopButtonLoading(button, restoreCartState = true) {
        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
        this.loadingButton = null;
        if (restoreCartState) {
          this.applyCartState(this.latestCart);
        } else {
          this.updateButtons({ disabled: false });
        }
      }
    }
  );
}
